<script setup lang="ts">
  import { getMonthIndex, formatDate, getStartOfDay } from '@travelata-libs/utils';
  import { computed } from 'vue';
  import { CalendarSize, type DateTimestamp, type DayCell, MonthName } from './Calendar.types';
  import CalendarDay from './CalendarDay.vue';
  import CalendarWeek from './CalendarWeek.vue';
  import { useCalendar } from './composables/useCalendar';
  import { buildClassLists, buildDayCells } from './utils/dayCell';
  import { Typo, TypoVariant } from '@/components';

  interface CalendarTemplateProps {
    dates: DateTimestamp[];
    templateDate: DateTimestamp;
    size?: `${CalendarSize}`;
    range?: boolean;
    mobile?: boolean;
    double?: boolean;
    scroll?: boolean;
    flexibleDays?: number | null;
    minDate?: Date | null;
    maxDate?: Date | null;
    disabledFromDate?: Date | null;
  }

  const props = withDefaults(defineProps<CalendarTemplateProps>(), {
    size: CalendarSize.M,
    range: false,
    mobile: false,
    double: false,
    scroll: false,
    minDate: null,
    maxDate: null,
    disabledFromDate: null,
    flexibleDays: null,
  });

  interface CalendarTemplateEmits {
    (e: 'click', value: DateTimestamp): void;
    (e: 'hover', value: DateTimestamp): void;
  }

  const emit = defineEmits<CalendarTemplateEmits>();

  const { systemModelValue, displayedDate } = useCalendar();

  const today = getStartOfDay(Date.now());

  const monthText = computed(() => {
    const month = getMonthIndex(new Date(props.templateDate));

    return `${Object.values(MonthName)[month]} ${formatDate(props.templateDate, 'yyyy')}`;
  });

  const monthVariant = computed(() => (props.scroll ? TypoVariant.BodyMMedium : TypoVariant.BodyLBold));

  const dayCells = computed(() =>
    buildDayCells(props.dates, props.templateDate, {
      minDate: props.minDate,
      maxDate: props.maxDate,
      disabledFromDate: props.disabledFromDate,
    }),
  );

  const classLists = computed(() =>
    buildClassLists(dayCells.value, systemModelValue.value, {
      flexibleDays: props.flexibleDays,
      range: props.range,
      double: props.double,
      today,
    }),
  );

  const onCellClick = (cell: DayCell) => {
    if (cell.isDisabled) {
      return;
    }
    if (cell.isOtherMonth && !props.double) {
      displayedDate.value = cell.ts;
    }
    emit('click', cell.ts);
  };

  const onCellHover = (cell: DayCell) => {
    if (props.mobile) {
      return;
    }
    if (cell.isDisabled) {
      return;
    }
    emit('hover', cell.ts);
  };
</script>

<template>
  <div
    :class="[
      'tl-calendar-template',
      `tl-calendar-template_size-${size}`,
      { 'tl-calendar-template_mobile': mobile, 'tl-calendar-template_scroll': scroll },
    ]"
  >
    <CalendarWeek v-if="!mobile" :size="size" class="tl-calendar-template__week" />

    <Typo v-if="mobile && double" :variant="monthVariant" class="tl-calendar-template__month">
      {{ monthText }}
    </Typo>
    <div class="tl-calendar-template__days">
      <CalendarDay
        v-for="(cell, index) in dayCells"
        :key="cell.ts"
        :day="cell.ts"
        :label="cell.label"
        :class-list="classLists[index]"
        :size="size"
        @click="onCellClick(cell)"
        @hover="onCellHover(cell)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tl-calendar-template {
    $root: &;

    &_size-l {
      padding: var(--spacing-s) 0 var(--spacing-m) 0;

      #{$root}__days {
        grid-template-columns: repeat(7, 54px);
      }
    }

    &_size-m {
      padding: var(--spacing-xs) 0 var(--spacing-l) 0;
    }

    &__week {
      border-bottom: 1px solid var(--color-neutral-30);
      margin-bottom: var(--spacing-xs);
    }

    &__month {
      color: var(--color-text-primary);
      text-align: center;
    }

    &__days {
      align-items: center;
      display: grid;
      grid-template-columns: repeat(7, 44px);
      row-gap: var(--spacing-xxxs);
    }

    &_mobile {
      padding: var(--spacing-s) var(--spacing-l);

      &:not(#{$root}_scroll):last-child {
        border-top: 1px dashed var(--color-text-tertiary);
      }

      &#{$root}_scroll {
        padding: var(--spacing-xs) 0 0;

        #{$root}__month {
          align-items: center;
          display: flex;
          height: 30px;
          justify-content: center;
        }
      }
    }
  }
</style>
